'use client'

import { Suspense } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import HintBanner from '@/components/HintBanner'
import PostsGenerationForm from '@/components/PostsGenerationForm'
import SocialPostGrid from '@/components/SocialPostGrid'
import SocialPostQueuePanel from '@/components/SocialPostQueuePanel'

type ScheduleTab = 'SCHEDULED' | 'PUBLISHED' | 'UNSCHEDULED'

const VALID_TABS: ScheduleTab[] = ['SCHEDULED', 'PUBLISHED', 'UNSCHEDULED']

function SocialPostsTabsInner() {
  const t = useTranslations('SocialPostsTabs')
  const searchParams = useSearchParams()
  const router = useRouter()

  const tabParam = (searchParams.get('tab')?.toUpperCase() ??
    'UNSCHEDULED') as ScheduleTab
  const activeTab: ScheduleTab = VALID_TABS.includes(tabParam)
    ? tabParam
    : 'UNSCHEDULED'

  const onTabChange = (value: string) => {
    const params = new URLSearchParams(searchParams.toString())
    params.set('tab', value.toLowerCase())
    // remove legacy platform param
    params.delete('platform')
    router.replace(`?${params.toString()}`)
  }

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:h-full lg:w-full">
      <div className="flex w-full flex-col gap-5 pb-6 lg:w-lg lg:shrink-0 lg:overflow-y-auto lg:pr-4 lg:pl-1 custom-scrollbar">
        <PostsGenerationForm />
      </div>
      <div className="flex flex-1 flex-col gap-6 min-h-0 lg:overflow-y-auto custom-scrollbar lg:pr-1">
        <SocialPostQueuePanel />
        <HintBanner>{t('clickPostHint')}</HintBanner>
        <Tabs
          value={activeTab}
          onValueChange={onTabChange}
          className="flex flex-col gap-6"
        >
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="SCHEDULED">{t('tabScheduled')}</TabsTrigger>
            <TabsTrigger value="PUBLISHED">{t('tabPublished')}</TabsTrigger>
            <TabsTrigger value="UNSCHEDULED">{t('tabUnscheduled')}</TabsTrigger>
          </TabsList>
          <TabsContent value="SCHEDULED" className="mt-0">
            <SocialPostGrid scheduleStatus="SCHEDULED" />
          </TabsContent>
          <TabsContent value="PUBLISHED" className="mt-0">
            <SocialPostGrid scheduleStatus="PUBLISHED" />
          </TabsContent>
          <TabsContent value="UNSCHEDULED" className="mt-0">
            <SocialPostGrid scheduleStatus="UNSCHEDULED" />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

export default function SocialPostsTabs() {
  return (
    <Suspense>
      <SocialPostsTabsInner />
    </Suspense>
  )
}
